import PropTypes from "prop-types"
import { useState, useEffect } from "react"
import { Modal } from "antd"
import Button from "components/ui/Button"
import {
  trackEvent,
  trackSegmentFormOpened,
  trackSegmentFormSubmission,
} from "lib/googleTagManager"
import submitHubspotForm from "lib/submitHubspotForm"
import * as css from "./ReachOutModal.module.css"

const FORM_ID = "5db2ee38-ca7a-43c1-ace3-d9fd2f69f999"

function ReachOutModal({ isOpen, onClose, initialMessage = "" }) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    company: "",
    jobtitle: "",
    message: initialMessage,
  })

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false)
      setError(null)
      setFormData({
        firstname: "",
        lastname: "",
        email: "",
        company: "",
        jobtitle: "",
        message: initialMessage,
      })
      trackEvent("contactSalesFormViewed", {
        formId: FORM_ID,
        sourcePage: "Events - Conference",
        pageSlug: typeof window !== "undefined" ? window.location.pathname : "",
        pageName: typeof document !== "undefined" ? document.title : "",
      })
      trackSegmentFormOpened({
        form_id: FORM_ID,
        form_name: "reach_out_conference",
        form_type: "contact_sales",
      })
    }
  }, [isOpen, initialMessage])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const response = await submitHubspotForm(FORM_ID, {
        ...formData,
        source_page: "Events - Conference",
      })

      if (response.ok) {
        setSubmitted(true)
        trackEvent("formSubmission", {
          formSubmissionEventName: "Reach Out - Conference",
          sourcePage: "Events - Conference",
        })
        trackSegmentFormSubmission({
          form_id: FORM_ID,
          form_name: "reach_out_conference",
          form_type: "contact_sales",
          email: formData.email,
        })
      } else {
        setError("Something went wrong. Please try again.")
      }
    } catch (_err) {
      setError("Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      centered
      destroyOnClose
    >
      <div className={css.content}>
        <div className={css.header}>
          <span className={css.label}>Contact Preset</span>
          <h2 className={css.title}>Talk to the Preset team</h2>
          {!submitted && (
            <p className={css.description}>
              Ready to learn more? Please fill out the form and we&apos;ll be in
              touch.
            </p>
          )}
        </div>

        {submitted ? (
          <div className={css.thankYou}>
            <svg
              className={css.checkIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <p>
              Thank you for reaching out! We will be in touch shortly. Please
              keep an eye out for emails from &quot;@preset.io&quot;
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={css.form}>
            <div className={css.nameRow}>
              <div className={css.field}>
                <label htmlFor="reach-out-firstname" className={css.fieldLabel}>
                  First Name <span className={css.required}>*</span>
                </label>
                <input
                  type="text"
                  id="reach-out-firstname"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                  required
                  className={css.input}
                />
              </div>

              <div className={css.field}>
                <label htmlFor="reach-out-lastname" className={css.fieldLabel}>
                  Last Name <span className={css.required}>*</span>
                </label>
                <input
                  type="text"
                  id="reach-out-lastname"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                  required
                  className={css.input}
                />
              </div>
            </div>

            <div className={css.field}>
              <label htmlFor="reach-out-email" className={css.fieldLabel}>
                Email <span className={css.required}>*</span>
              </label>
              <input
                type="email"
                id="reach-out-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={css.input}
              />
            </div>

            <div className={css.nameRow}>
              <div className={css.field}>
                <label htmlFor="reach-out-company" className={css.fieldLabel}>
                  Company <span className={css.required}>*</span>
                </label>
                <input
                  type="text"
                  id="reach-out-company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className={css.input}
                />
              </div>

              <div className={css.field}>
                <label htmlFor="reach-out-jobtitle" className={css.fieldLabel}>
                  Job Title <span className={css.required}>*</span>
                </label>
                <input
                  type="text"
                  id="reach-out-jobtitle"
                  name="jobtitle"
                  value={formData.jobtitle}
                  onChange={handleChange}
                  required
                  className={css.input}
                />
              </div>
            </div>

            <div className={css.field}>
              <label htmlFor="reach-out-message" className={css.fieldLabel}>
                Message
              </label>
              <textarea
                id="reach-out-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={css.textarea}
              />
            </div>

            {error && <p className={css.error}>{error}</p>}

            <Button
              type="submit"
              color="lush"
              size="medium"
              disabled={submitting}
              className={css.submitButton}
            >
              {submitting ? "Sending..." : "Submit"}
            </Button>
          </form>
        )}
      </div>
    </Modal>
  )
}

ReachOutModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialMessage: PropTypes.string,
}

export default ReachOutModal
